"use client";

import { toast as sonnerToast } from "sonner";
import { AgentToast } from "@/components/ui/AgentToast";
import { SimpleToast } from "@/components/ui/SimpleToast";
interface AgentToastOptions {
  title: string;
  message: string;
  duration?: number;
  className?: string;
}

// Base toast styling with primary-900 background
const baseToastStyle = {
  backgroundColor: "var(--primary-900)",
  borderColor: "var(--primary-800)",
  color: "white"
};
const baseToastClassName = "bg-[var(--primary-900)] border-[var(--primary-800)] text-white";

// Agent toast for notifications with icon
export function agentToast({
  title,
  message,
  duration = 5000,
  className
}: AgentToastOptions) {
  return sonnerToast.info(<AgentToast title={title} message={message} className={className} />, {
    duration,
    className: baseToastClassName,
    style: baseToastStyle,
    icon: null
  });
}

// Task-specific toasts with consistent styling
export function taskSuccess(message: string, description?: string, duration = 4000) {
  const content = description ? <div>
      <div className="font-semibold">{message}</div>
      <div className="opacity-90 text-sm">{description}</div>
    </div> : message;
  return sonnerToast.success(<SimpleToast message={content} />, {
    duration,
    className: baseToastClassName,
    style: baseToastStyle,
    icon: null
  });
}
export function taskError(message: string, duration = 6000) {
  return sonnerToast.error(<SimpleToast message={message} />, {
    duration,
    className: baseToastClassName,
    style: baseToastStyle,
    icon: null
  });
}
export function taskWarning(message: string, duration = 5000, id?: string) {
  return sonnerToast.warning(<SimpleToast message={message} />, {
    id,
    // Sonner will deduplicate toasts with the same id
    duration,
    className: baseToastClassName,
    style: baseToastStyle,
    icon: null
  });
}
export function taskInfo(message: string, duration = 4000) {
  return sonnerToast.info(<SimpleToast message={message} />, {
    duration,
    className: baseToastClassName,
    style: baseToastStyle,
    icon: null
  });
}

// Version update toast with action button
export function versionUpdateToast(message: string, action: {
  label: string;
  onClick: () => void;
}) {
  return sonnerToast.info(<SimpleToast message={message} />, {
    action,
    duration: Infinity,
    className: baseToastClassName,
    style: baseToastStyle,
    icon: null
  });
}

// Wrapper toast object for direct imports (e.g., import { toast } from "@/lib/toast")
export const toast = {
  success: (message: string, options?: Parameters<typeof sonnerToast.success>[1]) => {
    return sonnerToast.success(<SimpleToast message={message} />, {
      ...options,
      className: baseToastClassName,
      style: baseToastStyle,
      icon: null
    });
  },
  error: (message: string, options?: Parameters<typeof sonnerToast.error>[1]) => {
    return sonnerToast.error(<SimpleToast message={message} />, {
      ...options,
      className: baseToastClassName,
      style: baseToastStyle,
      icon: null
    });
  },
  warning: (message: string, options?: Parameters<typeof sonnerToast.warning>[1]) => {
    return sonnerToast.warning(<SimpleToast message={message} />, {
      ...options,
      className: baseToastClassName,
      style: baseToastStyle,
      icon: null
    });
  },
  info: (message: string, options?: Parameters<typeof sonnerToast.info>[1]) => {
    return sonnerToast.info(<SimpleToast message={message} />, {
      ...options,
      className: baseToastClassName,
      style: baseToastStyle,
      icon: null
    });
  },
  // Re-export other sonner methods that might be used
  message: sonnerToast.message,
  promise: sonnerToast.promise,
  custom: sonnerToast.custom,
  dismiss: sonnerToast.dismiss,
  loading: sonnerToast.loading
};